import React, { useEffect, useRef, useState } from "react";
import { Video, X, Users, Clock, CalendarDays } from "lucide-react";
import { api } from "./api";
import { useAuth } from "./AuthContext";

/**
 * Global meeting-invitations watcher.
 *
 * Fires a centred popup on the invitee's screen for two events:
 *
 *   1. LIVE — a meeting the current user is a participant of is currently in
 *      progress (backend sets `status: "live"` when the host starts it).
 *      Popup shows immediately with a "Join Now" button.
 *
 *   2. SCHEDULED — a meeting scheduled for a future time. A client-side
 *      setTimeout is armed to fire the same popup exactly at `scheduled_at`.
 *      The user's calendar entry is auto-created server-side when the meeting
 *      is created (already implemented in `POST /meetings`), so this component
 *      only needs to handle the "time-of-meeting" notification.
 *
 * Dismissed meeting IDs are persisted in localStorage so the popup isn't
 * repeatedly shown for the same event.
 */

const POLL_INTERVAL_MS = 10_000;
const SEEN_KEY = "hexa_meeting_seen";
const DISMISS_KEY = "hexa_meeting_dismissed";

export default function MeetingInvitationsWatcher() {
  const { user, isAuthenticated } = useAuth();
  const [invite, setInvite] = useState(null);   // { meeting, reason: "live" | "scheduled" }
  const timersRef = useRef(new Map());          // meetingId -> setTimeout id

  const myEmail = (user?.address || user?.email || "").toLowerCase();

  useEffect(() => {
    if (!isAuthenticated || !myEmail) return;
    let stopped = false;
    let firstLoad = true;
    try {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    } catch { /* noop */ }

    const getSeen = () => {
      try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}"); } catch { return {}; }
    };
    const saveSeen = (obj) => {
      try { localStorage.setItem(SEEN_KEY, JSON.stringify(obj)); } catch { /* noop */ }
    };
    const browserNotify = (m) => {
      try {
        if (!("Notification" in window) || Notification.permission !== "granted") return;
        const when = m.scheduled_at ? new Date(m.scheduled_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";
        const n = new Notification(`Meeting invite: ${m.title || "Untitled meeting"}`, {
          body: `From ${m.host || "—"}${when ? " · " + when : ""}`,
          tag: `hexa-invite-${m.id}`,
        });
        n.onclick = () => { try { window.focus(); } catch { /* noop */ } n.close(); };
      } catch { /* noop */ }
    };

    const load = async () => {
      try {
        const { data } = await api.get("/meetings");
        if (stopped) return;
        handleMeetings(Array.isArray(data) ? data : []);
      } catch { /* silently ignore — polling will retry */ }
    };

    const isDismissed = (mid, reason) => {
      try {
        const raw = localStorage.getItem(DISMISS_KEY);
        const obj = raw ? JSON.parse(raw) : {};
        return !!obj[`${mid}:${reason}`];
      } catch { return false; }
    };

    const isRelevant = (m) => {
      // Show for meetings I host OR am a participant in (any casing).
      const parts = (m.participants || []).map(p => String(p).toLowerCase());
      const host = String(m.host || "").toLowerCase();
      return parts.includes(myEmail) || host === myEmail;
    };

    const handleMeetings = (meetings) => {
      const now = Date.now();
      const seen = getSeen();
      let seenChanged = false;
      meetings.forEach(m => {
        if (!isRelevant(m)) return;
        if (m.status === "ended") return;

        // NEW INVITE (Support #301778): someone else scheduled a meeting with me.
        if (!seen[m.id]) {
          seen[m.id] = Date.now();
          seenChanged = true;
          const hostAddr = String(m.host || "").toLowerCase();
          if (!firstLoad && hostAddr !== myEmail && m.status !== "live" && !isDismissed(m.id, "invited")) {
            setInvite(prev => prev || { meeting: m, reason: "invited" });
            browserNotify(m);
          }
        }

        // LIVE: host has started this — show popup immediately.
        if (m.status === "live" && !isDismissed(m.id, "live")) {
          setInvite(prev => prev || { meeting: m, reason: "live" });
          return;
        }

        // SCHEDULED: arm a timer that fires exactly at scheduled_at.
        if (m.status === "scheduled" && m.scheduled_at && !isDismissed(m.id, "scheduled")) {
          const ts = new Date(m.scheduled_at).getTime();
          if (Number.isNaN(ts)) return;
          const delay = ts - now;
          // If we're within a 60 second window either side of the scheduled
          // time, fire the popup immediately.
          if (delay <= 60_000 && delay >= -60_000) {
            setInvite(prev => prev || { meeting: m, reason: "scheduled" });
            return;
          }
          // If the meeting is in the future (up to 24h ahead), arm a timer.
          if (delay > 0 && delay <= 24 * 60 * 60_000) {
            if (timersRef.current.has(m.id)) return;
            const id = setTimeout(() => {
              setInvite(prev => prev || { meeting: m, reason: "scheduled" });
              timersRef.current.delete(m.id);
            }, delay);
            timersRef.current.set(m.id, id);
          }
        }
      });
      if (seenChanged) saveSeen(seen);
      firstLoad = false;
    };

    load();
    const iv = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      clearInterval(iv);
      timersRef.current.forEach(id => clearTimeout(id));
      timersRef.current.clear();
    };
  }, [isAuthenticated, myEmail]);

  const dismiss = (reasonOverride) => {
    if (!invite) return;
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      obj[`${invite.meeting.id}:${reasonOverride || invite.reason}`] = Date.now();
      localStorage.setItem(DISMISS_KEY, JSON.stringify(obj));
    } catch { /* noop */ }
    setInvite(null);
  };

  const joinNow = () => {
    if (!invite) return;
    // Mark dismissed so it doesn't re-open, then dispatch an event Meetings.jsx
    // listens to so it opens the correct room automatically.
    dismiss();
    window.dispatchEvent(new CustomEvent("hexa:open-app", { detail: { id: "meetings", meetingId: invite.meeting.id } }));
  };

  if (!invite) return null;
  const m = invite.meeting;
  const when = m.scheduled_at ? new Date(m.scheduled_at) : null;
  const timeLabel = when ? when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <div
      data-testid="meeting-invite-popup"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border"
        style={{
          background: "linear-gradient(135deg, #0b1220 0%, #14203b 100%)",
          borderColor: "rgba(255,122,46,0.35)",
          boxShadow: "0 20px 60px rgba(255,122,46,0.25), 0 0 0 1px rgba(139,92,246,0.15)",
          animation: "hexa-invite-in 260ms cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <style>{`
          @keyframes hexa-invite-in {
            0%   { transform: scale(0.9) translateY(20px); opacity: 0; }
            100% { transform: scale(1)   translateY(0);    opacity: 1; }
          }
          @keyframes hexa-invite-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255,122,46,0.55); }
            50%      { box-shadow: 0 0 0 14px rgba(255,122,46,0); }
          }
        `}</style>

        {/* Header */}
        <div className="relative px-5 pt-5 pb-4">
          <button
            onClick={() => dismiss()}
            data-testid="meeting-invite-close"
            className="absolute top-3 right-3 p-1.5 rounded-full text-white/50 hover:text-white/90 hover:bg-white/10"
            title="Dismiss"
          >
            <X size={14} />
          </button>
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(135deg, #ff7a2e 0%, #f97316 100%)", animation: invite.reason === "live" ? "hexa-invite-pulse 1.8s ease-in-out infinite" : undefined }}
            >
              <Video size={22} color="#0b1220" />
            </div>
            <div className="min-w-0">
              <div className="text-[9px] mono uppercase tracking-[0.28em]" style={{ color: invite.reason === "live" ? "#ff7a2e" : "#a78bfa" }}>
                {invite.reason === "live" ? "· Meeting live now" : invite.reason === "invited" ? "· New meeting invite" : "· Scheduled meeting starting"}
              </div>
              <div className="text-white text-lg font-semibold tracking-tight truncate">
                {m.title || "Untitled meeting"}
              </div>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="px-5 pb-4 space-y-1.5 text-[12px] text-white/70">
          <div className="flex items-center gap-2">
            <Users size={12} className="text-white/40" />
            Host <span className="text-white/90 font-medium">{m.host || "—"}</span>
            {m.participants?.length > 0 && (
              <span className="text-white/40 mono text-[10px]">· {m.participants.length} participant{m.participants.length !== 1 ? "s" : ""}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <CalendarDays size={12} className="text-white/40" />
            <span className="text-white/90">{timeLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={12} className="text-white/40" />
            {m.duration_min || 30} minutes · Room <span className="mono text-white/90">{m.room_id}</span>
          </div>
          {m.agenda && (
            <div className="mt-3 rounded-lg bg-white/5 border border-white/10 p-2 text-[11px] mono text-white/70 max-h-24 overflow-y-auto whitespace-pre-wrap">
              {m.agenda}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 flex items-center gap-2">
          <button
            onClick={() => dismiss()}
            data-testid="meeting-invite-later"
            className="flex-1 py-2.5 rounded-xl border border-white/10 text-white/70 text-sm font-medium hover:bg-white/5"
          >
            Later
          </button>
          <button
            onClick={joinNow}
            data-testid="meeting-invite-join"
            className="flex-[1.6] py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            style={{
              background: "linear-gradient(135deg, #ff7a2e 0%, #f97316 100%)",
              color: "#0b1220",
              boxShadow: "0 6px 20px rgba(255,122,46,0.35)",
            }}
          >
            <Video size={14} /> Join Now
          </button>
        </div>
      </div>
    </div>
  );
}
